<template>
  <manual-page
    title="Programa de Producción"
    description="Guía completa para planificar, organizar y ejecutar la producción de alimentos de manera eficiente."
    :show-video="true"
    :related-links="relatedLinks"
    :action-route="{ name: 'productionScheduleIndex' }"
    action-text="Ir a Programa de Producción"
  >
    <!-- Overview -->
    <h2>Vista General</h2>
    <p>
      El Programa de Producción es tu herramienta principal para planificar el trabajo diario.
      Te permite ver las órdenes agrupadas por fecha de entrega y organizar la producción
      de manera eficiente.
    </p>

    <div class="info-box">
      <p class="font-medium text-gray-700 mb-2">Con el Programa de Producción puedes:</p>
      <ul class="list-disc list-inside text-gray-600 space-y-1">
        <li>Visualizar todas las órdenes por fecha de entrega</li>
        <li>Agrupar producción por tipo de alimento</li>
        <li>Calcular cantidades totales de ingredientes</li>
        <li>Procesar múltiples órdenes en lote</li>
        <li>Planificar con anticipación la carga de trabajo</li>
        <li>Evitar retrasos en las entregas</li>
      </ul>
    </div>

    <!-- Calendar View -->
    <h2>Vista de Calendario</h2>
    <p>
      El programa muestra las órdenes organizadas por fecha de entrega.
      Cada día muestra un resumen de las órdenes programadas.
    </p>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">1</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Seleccionar Fecha</h3>
          <p class="text-gray-600">
            Usa el calendario o los controles de navegación para seleccionar
            el día o rango de fechas que deseas planificar.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">2</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Ver Resumen del Día</h3>
          <p class="text-gray-600 mb-2">
            Cada día muestra:
          </p>
          <ul class="list-disc list-inside text-gray-600 space-y-1">
            <li>Número total de órdenes programadas</li>
            <li>Desglose por tipo de alimento</li>
            <li>Cantidad de kilos a producir</li>
            <li>Órdenes de envío vs recolección</li>
          </ul>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">3</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Ver Detalle</h3>
          <p class="text-gray-600">
            Haz clic en una fecha para ver el listado completo de órdenes
            programadas para ese día y poder procesarlas.
          </p>
        </div>
      </div>
    </div>

    <div class="tip-box">
      <p class="text-sm text-blue-700">
        <strong>Tip:</strong> Revisa el programa con 2-3 días de anticipación para
        asegurarte de tener todos los ingredientes necesarios.
      </p>
    </div>

    <!-- Production Planning -->
    <h2>Planificación de Producción</h2>
    <p>
      Una buena planificación te permite optimizar recursos y garantizar entregas a tiempo.
    </p>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">1</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Revisar Órdenes Pendientes</h3>
          <p class="text-gray-600">
            Comienza revisando todas las órdenes pagadas que aún no están en preparación.
            Identifica cuáles tienen fecha de entrega más próxima.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">2</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Agrupar por Tipo de Alimento</h3>
          <p class="text-gray-600">
            Agrupa las órdenes del mismo tipo de dieta. Esto permite preparar
            ingredientes base en común y optimizar el uso de equipos.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">3</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Verificar Inventario</h3>
          <p class="text-gray-600">
            Antes de iniciar, verifica que tengas suficientes ingredientes.
            Si falta algo, planifica la compra con anticipación.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">4</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Estimar Tiempo</h3>
          <p class="text-gray-600">
            Considera el tiempo necesario para preparar, empacar y limpiar.
            Distribuye la carga de trabajo de manera realista.
          </p>
        </div>
      </div>
    </div>

    <!-- Batch Processing -->
    <h2>Producción en Lote</h2>
    <p>
      Producir en lotes ahorra tiempo y recursos. Agrupa órdenes similares
      para maximizar la eficiencia.
    </p>

    <div class="info-box">
      <p class="font-medium text-gray-700 mb-2">Ventajas de producir en lote:</p>
      <ul class="list-disc list-inside text-gray-600 space-y-1">
        <li>Menor tiempo de preparación por orden</li>
        <li>Optimización de ingredientes base</li>
        <li>Menos cambios de configuración en equipos</li>
        <li>Reducción de merma</li>
        <li>Menos tiempo de limpieza entre lotes</li>
      </ul>
    </div>

    <h3>Cómo Agrupar Órdenes</h3>
    <p>Criterios para agrupar órdenes en lotes:</p>
    <ul>
      <li><strong>Por tipo de dieta:</strong> BARF, cocido, mixto</li>
      <li><strong>Por proteína principal:</strong> Res, pollo, pavo</li>
      <li><strong>Por fecha de entrega:</strong> Mismo día o días consecutivos</li>
      <li><strong>Por tipo de entrega:</strong> Envíos juntos, recolecciones juntas</li>
    </ul>

    <div class="tip-box">
      <p class="text-sm text-blue-700">
        <strong>Tip:</strong> Prepara primero los alimentos que requieren más tiempo
        de refrigeración o congelación para que estén listos a tiempo.
      </p>
    </div>

    <!-- Executing Production -->
    <h2>Ejecución de la Producción</h2>

    <h3>Iniciar Preparación</h3>
    <p>Para comenzar a producir un lote de órdenes:</p>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">1</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Seleccionar Órdenes</h3>
          <p class="text-gray-600">
            Marca las órdenes que vas a preparar. Puedes seleccionar
            múltiples órdenes del mismo tipo para procesarlas juntas.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">2</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Iniciar Preparación</h3>
          <p class="text-gray-600">
            Haz clic en "Iniciar preparación". Las órdenes cambian
            a estado "En preparación", indicando que estás trabajando en ellas.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">3</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Ver Detalle de Producción</h3>
          <p class="text-gray-600">
            El sistema muestra un resumen con las cantidades totales
            de ingredientes necesarios para el lote seleccionado.
          </p>
        </div>
      </div>
    </div>

    <h3>Elaborar el Alimento</h3>
    <p>Durante la preparación, ten en cuenta:</p>
    <ul>
      <li><strong>Sigue las recetas:</strong> Respeta las proporciones de cada dieta</li>
      <li><strong>Pesa con precisión:</strong> Usa básculas calibradas</li>
      <li><strong>Etiqueta correctamente:</strong> Identifica cada porción con nombre de mascota</li>
      <li><strong>Mantén la cadena de frío:</strong> Trabaja en ambiente adecuado</li>
      <li><strong>Registra anomalías:</strong> Documenta cualquier problema o cambio</li>
    </ul>

    <h3>Finalizar Preparación</h3>
    <p>Al terminar de preparar un lote:</p>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">1</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Verificar Empaque</h3>
          <p class="text-gray-600">
            Asegúrate de que cada orden esté correctamente empacada
            y etiquetada con el nombre del cliente y mascota.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">2</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Marcar como Listas</h3>
          <p class="text-gray-600">
            Selecciona las órdenes completadas y haz clic en "Marcar como listas".
            Pasan a estado "Lista para envío" o "Lista para recolección".
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">3</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Almacenar Correctamente</h3>
          <p class="text-gray-600">
            Coloca las órdenes listas en el área designada, organizadas
            por tipo de entrega y fecha para facilitar el despacho.
          </p>
        </div>
      </div>
    </div>

    <div class="warning-box">
      <p class="text-sm text-yellow-800">
        <strong>Importante:</strong> No marques órdenes como listas hasta que estén
        completamente empacadas y etiquetadas. Una orden "lista" indica que puede
        entregarse inmediatamente.
      </p>
    </div>

    <!-- Ingredient Calculation -->
    <h2>Cálculo de Ingredientes</h2>
    <p>
      El sistema puede ayudarte a calcular las cantidades totales de ingredientes
      necesarios para un grupo de órdenes.
    </p>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">1</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Ver Resumen de Ingredientes</h3>
          <p class="text-gray-600">
            Al seleccionar órdenes para producir, el sistema muestra
            la suma total de cada ingrediente necesario.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">2</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Comparar con Inventario</h3>
          <p class="text-gray-600">
            Compara las cantidades necesarias con tu inventario actual
            para identificar si necesitas comprar algo.
          </p>
        </div>
      </div>
    </div>

    <div class="tip-box">
      <p class="text-sm text-blue-700">
        <strong>Tip:</strong> Revisa el cálculo de ingredientes el día anterior
        para asegurarte de tener todo listo y evitar pausas durante la producción.
      </p>
    </div>

    <!-- Delivery Coordination -->
    <h2>Coordinación de Entregas</h2>
    <p>
      Una vez que las órdenes están listas, coordina las entregas según su tipo.
    </p>

    <h3>Órdenes para Envío</h3>
    <ul>
      <li>Agrupa por zona de entrega si usas transportista propio</li>
      <li>Prepara las guías de paquetería con anticipación</li>
      <li>Coordina la recolección con el servicio de mensajería</li>
      <li>Verifica que cada paquete tenga su etiqueta visible</li>
    </ul>

    <h3>Órdenes para Recolección</h3>
    <ul>
      <li>Organiza las órdenes por orden alfabético o de llegada esperada</li>
      <li>Mantenlas en un área de fácil acceso</li>
      <li>El cliente recibe notificación automática cuando está lista</li>
      <li>Verifica la identidad del cliente al entregar</li>
    </ul>

    <!-- Best Practices -->
    <h2>Buenas Prácticas</h2>
    <ul>
      <li><strong>Planifica con anticipación:</strong> Revisa el programa al menos un día antes</li>
      <li><strong>Prepara el área:</strong> Limpia y organiza antes de comenzar</li>
      <li><strong>Trabaja por lotes:</strong> Agrupa órdenes similares para mayor eficiencia</li>
      <li><strong>Etiqueta inmediatamente:</strong> No dejes porciones sin identificar</li>
      <li><strong>Mantén el sistema actualizado:</strong> Cambia estados en tiempo real</li>
      <li><strong>Documenta problemas:</strong> Registra cualquier incidencia para seguimiento</li>
      <li><strong>Controla el inventario:</strong> Registra los insumos utilizados</li>
    </ul>

    <!-- Support -->
    <h2>¿Necesitas ayuda?</h2>
    <p>Si tienes problemas con el programa de producción:</p>
    <ul>
      <li><strong>WhatsApp:</strong> +52 811 669 1329</li>
      <li><strong>Correo:</strong> soporte@trunimal.com</li>
    </ul>
  </manual-page>
</template>

<script>
import ManualPage from "@/components/manual/ManualPage.vue";

export default {
  name: "ManualProductionSchedule",
  components: { ManualPage },
  data() {
    return {
      relatedLinks: [
        { route: "manualOrders", label: "Órdenes" },
        { route: "manualInventory", label: "Inventario" },
        { route: "manualPets", label: "Mascotas" },
        { route: "manualGettingStarted", label: "Primeros Pasos" },
      ],
    };
  },
};
</script>
